import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { User, Mail, Phone, Clock, Tag, BarChart3, StickyNote, Loader2, ShoppingBag, DollarSign, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCustomerProfile } from '../hooks/useConversations';
import { PANEL_FADE } from '../lib/motion';

export const CustomerProfilePanel = ({ conversationId }: { conversationId: string }) => {
  const { profile, isLoading } = useCustomerProfile(conversationId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 text-center text-xs text-slate-400">
        No customer profile linked for this conversation.
      </div>
    );
  }

  const lastSeen = new Date(profile.lastSeen);

  return (
    <motion.div
      key={conversationId}
      variants={PANEL_FADE}
      initial="hidden"
      animate="show"
      className="flex flex-col h-full overflow-y-auto"
    >
      {/* Header Pod */}
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
            <User className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Customer Profile
          </span>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 px-2 py-0.2 text-[9px] font-semibold text-slate-600 dark:text-slate-300 shadow-2xs">
          Synced
        </span>
      </div>

      <div className="p-3.5 space-y-3">
        {/* Profile Card */}
        <div className="text-center rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/60 p-3.5 shadow-2xs">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white dark:bg-slate-800 font-bold text-sm text-slate-700 dark:text-slate-200 shadow-xs border border-slate-200/80 dark:border-slate-700">
            {profile.name ? profile.name.charAt(0).toUpperCase() : <User className="h-6 w-6 text-slate-400 dark:text-slate-500" />}
          </div>
          <h3 className="text-xs font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide">{profile.name}</h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">{profile.location || 'Unknown Location'}</p>
        </div>

        {/* Contact Pod */}
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Contact Details</span>
            <Mail className="h-3 w-3 text-slate-400 dark:text-slate-500" />
          </div>
          <div className="p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Email</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[160px] text-right">{profile.email}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Phone</span>
              <span className="font-semibold text-slate-800 dark:text-slate-200">{profile.phone}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Last Active</span>
              <span className="text-[11px] text-slate-600 dark:text-slate-300 font-medium">{formatDistanceToNow(lastSeen, { addSuffix: true })}</span>
            </div>
          </div>
        </Card>

        {/* Shopify / E-commerce Telemetry Pod */}
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Store Telemetry</span>
            <ShoppingBag className="h-3 w-3 text-indigo-500 dark:text-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-2 p-2.5 bg-slate-50/30 dark:bg-slate-950/40">
            <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-850 p-2 text-center shadow-2xs">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Orders</span>
              <span className="text-sm font-black text-slate-900 dark:text-slate-100">{profile.totalOrders}</span>
            </div>
            <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-850 p-2 text-center shadow-2xs">
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Total Spent</span>
              <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">{profile.totalSpent}</span>
            </div>
          </div>
        </Card>

        {/* Tags Pod */}
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Tags & Segments</span>
            <Tag className="h-3 w-3 text-slate-400 dark:text-slate-500" />
          </div>
          <div className="p-2.5 flex flex-wrap gap-1">
            {profile.tags && profile.tags.length > 0 ? (
              profile.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:text-slate-300"
                >
                  #{tag}
                </span>
              ))
            ) : (
              <span className="text-[11px] text-slate-400 dark:text-slate-500 italic">No tags assigned</span>
            )}
          </div>
        </Card>

        {/* Notes Pod */}
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-850 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">Internal Notes</span>
            <StickyNote className="h-3 w-3 text-slate-400 dark:text-slate-500" />
          </div>
          <div className="p-3">
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed bg-slate-50/70 dark:bg-slate-850 rounded-xl p-2.5 border border-slate-100 dark:border-slate-800">
              {profile.notes || 'No internal notes recorded yet.'}
            </p>
            <Button variant="outline" size="sm" className="mt-2.5 h-7.5 w-full rounded-xl border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
              Edit Notes
            </Button>
          </div>
        </Card>
      </div>
    </motion.div>
  );
};
